import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import { useCallback, useEffect, useState } from "react";
import { KahunPanelGroup, KahunPanelSubGroup, Relations } from "shared";
import { DataInputPanel } from "../dataInputTypes";
import {
  QualifiedFeature,
  Qualifier,
  QualifierFragment,
} from "fe-common/graphql";
import { useDiagnosticAgentState } from "../../context/diagnosticAgent";
import { usePatientCase } from "./usePatientCase";

const filterProcedureFindings = (
  qualifiers: Qualifier[],
): QualifierFragment[] => {
  return qualifiers
    ? qualifiers.filter((q) => q.relation.id === Relations.PROCEDURE_FINDING)
    : [];
};

export async function getProcedurePanels(panelsQfs: QualifiedFeature[], dao) {
  let panelsPromises = [];
  if (panelsQfs && panelsQfs.length > 0) {
    panelsQfs.forEach((panelQf) => {
      const featureId =
        panelQf.qualifiers &&
        panelQf.qualifiers.length > 0 &&
        panelQf.qualifiers[0].value
          ? panelQf.qualifiers[0].value.id
          : panelQf.feature.id;
      panelsPromises.push(dao.searchQualifiers("", featureId, ""));
    });
  }

  const addedPanels = [];

  const values = await Promise.all(panelsPromises);
  values.forEach((qualifiers, index) => {
    const qf = panelsQfs[index];
    const procedureQualifiers = filterProcedureFindings(qualifiers);
    const feature =
      qf.qualifiers && qf.qualifiers.length > 0 && qf.qualifiers[0].value
        ? qf.qualifiers[0].value
        : qf.feature;

    const newPanel = {
      title: feature.name,
      shortname: "9999",
      id: feature.id,
      findings: procedureQualifiers
        ? procedureQualifiers.map((q) => {
            return {
              ...qf,
              feature: {
                ...qf.feature,
                ...feature,
              },
              mandatory: null,
              qualifiers: [q],
            };
          })
        : [],
      panelGroup: {
        id: KahunPanelGroup.WORKUP,
        name: KahunPanelGroup.WORKUP,
        shortname: KahunPanelGroup.WORKUP,
      },
      panelSubgroup: {
        id: KahunPanelSubGroup.Procedures,
        name: "Procedures",
        shortname: "Kahun Procedures SUB group",
      },
      customFindings: [],
    };
    addedPanels.push(newPanel);
  });

  return addedPanels;
}

const emptyPanels = new Map();

export function useSmartPanels(): {
  loading: boolean;
  smartPanels: Map<string, DataInputPanel>;
  addProcedurePanel: any;
} {
  let dao = useDaoContext();
  let [state, setState] = useDiagnosticAgentState();
  let [loading, setLoading] = useState(!state.smartPanels);

  const {
    handlePanelsToAdd,
    patientCase: { panelsToAdd },
  } = usePatientCase();

  const loadSmartPanels = useCallback(
    async (panelsQfs: QualifiedFeature[]) => {
      return getProcedurePanels(panelsQfs, dao);
    },
    [dao],
  );

  const addProcedurePanel = useCallback(
    async (qf: QualifiedFeature) => {
      if (
        !panelsToAdd.find(
          (panel) => panel.qualifiers[0].value.id === qf.qualifiers[0].value.id,
        )
      ) {
        handlePanelsToAdd(qf);
      }

      const addedPanels = await loadSmartPanels([qf]);
      const newPanel = addedPanels[0];
      let smartPanels = new Map(state.smartPanels);
      smartPanels.set(newPanel.title, newPanel);
      setState((state) => ({ ...state, smartPanels }));
      return newPanel;
    },
    [handlePanelsToAdd, state, setState, loadSmartPanels, panelsToAdd],
  );

  const getSmartPanels = useCallback(async () => {
    const loadedPanels = await loadSmartPanels(panelsToAdd);
    let smartPanels = new Map();
    loadedPanels.forEach((addedPanel) =>
      smartPanels.set(addedPanel.title, addedPanel),
    );
    setState((state) => ({ ...state, smartPanels }));
  }, [loadSmartPanels, setState, panelsToAdd]);

  useEffect(() => {
    if (!state.smartPanels) {
      setLoading(true);
      (async () => {
        await getSmartPanels();
        setLoading(false);
      })();
    }
  }, [getSmartPanels, state.smartPanels]);

  return {
    loading,
    smartPanels: state.smartPanels || emptyPanels,
    addProcedurePanel,
  };
}
